import { csrfToken } from "utils/csrf-token"

import {
  SECTION_KINDS,
  SearchResponseItem,
  SearchResult,
  SearchSectionKind,
  SearchSection,
  SearchItem,
  SnowCountry,
  MountainCountry,
  SnowLocation,
  MountainLocation,
  WeatherLocation,
  SurfLocation,
  TideLocation,
  MountainCity,
} from "./data"

type SearchResponseSection = {
  title?: string
  items: SearchResponseItem[]
}

type SearchResponse = {
  main?: SearchResponseSection
  other?: SearchResponseSection
  nearby?: SearchResponseSection
}

export class LocationSearchApi {
  private cache: Map<string, SearchResult>

  constructor() {
    this.cache = new Map()
  }

  async get(rawQuery: string): Promise<SearchResult> {
    const query = rawQuery.trim().toLowerCase()
    if (query === "") return []

    const cached = this.getFromCache(query)
    if (cached !== undefined) return cached

    const response = await fetch("/location_search", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ query }),
    })

    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)

    const json: SearchResponse = await response.json()
    const result: SearchResult = []
    SECTION_KINDS.forEach((kind) => {
      const section = this.wrapSection(json, kind)
      if (section) result.push(section)
    })
    this.save(query, result)
    return result
  }

  private save(key: string, value: SearchResult) {
    this.cache.set(key, value)
  }

  private getFromCache(key: string) {
    return this.cache.get(key)
  }

  private wrapSection(
    response: SearchResponse,
    kind: SearchSectionKind,
  ): SearchSection | undefined {
    const section = response[kind]
    if (!section || section.items.length === 0) return undefined

    const items: SearchItem[] = []
    section.items.forEach((item) => {
      const wrapped = this.wrapItem(item)
      if (wrapped) items.push(wrapped)
    })

    return { kind, title: section.title, items }
  }

  // eslint-disable-next-line class-methods-use-this
  private wrapItem(item: SearchResponseItem): SearchItem | undefined {
    const klass = item.type
    switch (klass) {
      case "SnowCountry":
        return new SnowCountry(item)
      case "MountainCountry":
        return new MountainCountry(item)
      case "MountainCity":
        return new MountainCity(item)
      case "SnowLocation":
        return new SnowLocation(item)
      case "MountainLocation":
        return new MountainLocation(item)
      case "SurfLocation":
        return new SurfLocation(item)
      case "TideLocation":
        return new TideLocation(item)
      case "WeatherLocation":
      case "WeatherSmallLocation":
        return new WeatherLocation(item)
      default:
        return undefined
    }
  }
}
